import { z } from 'zod';

/**
 * SSL Certificate Status Levels
 */
export const SslStatusLevel = z.enum([
  'OK', // >30 days remaining
  'WARNING', // ≤30 days remaining
  'CRITICAL', // ≤7 days remaining
  'EXPIRED', // <0 days remaining
  'UNKNOWN', // SSL_CERT_EXPIRY_DATE not set
]);

export type SslStatusLevel = z.infer<typeof SslStatusLevel>;

/**
 * SSL Certificate Status Response
 *
 * Used by admin dashboard to display certificate expiry warnings
 */
export const SslStatusResponseSchema = z.object({
  level: SslStatusLevel,
  expiryDate: z.string().optional(), // ISO 8601 date string
  daysRemaining: z.number().optional(),
  message: z.string(),
});

export type SslStatusResponse = z.infer<typeof SslStatusResponseSchema>;

// === V2: Certificate Detail (parsed from PEM at runtime) ===
export const SslCertificateDetailSchema = z.object({
  commonName: z.string(),
  issuer: z.string(),
  domains: z.array(z.string()),
  serialNumber: z.string(),
  validFrom: z.string(),
  validTo: z.string(),
  daysRemaining: z.number(),
  keySize: z.number(),
  fingerprint: z.string(),
});
export type SslCertificateDetail = z.infer<typeof SslCertificateDetailSchema>;

// Enhanced status response (V2 — extends V1 SslStatusResponseSchema)
export const SslStatusResponseV2Schema = SslStatusResponseSchema.extend({
  certificate: SslCertificateDetailSchema.optional(),
  keystoreStatus: z.enum(['VALID', 'EXPIRED', 'MISSING', 'MISMATCH']).optional(),
  lastChecked: z.string().optional(),
});
export type SslStatusResponseV2 = z.infer<typeof SslStatusResponseV2Schema>;

// Secret key list (values never exposed)
export const SslSecretKeySchema = z.object({
  key: z.string(),
  isSet: z.boolean(),
  updatedAt: z.string().optional(),
});
export const SslSecretKeysSchema = z.object({
  keys: z.array(SslSecretKeySchema),
});
export type SslSecretKeys = z.infer<typeof SslSecretKeysSchema>;
